"use client";

import { useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

const VIDEO_SRC = "/newhero.mp4";

/**
 * First frame, served as a still. If autoplay is refused — Low Power Mode is
 * the common case, and no attribute combination overrides it — the cover shows
 * this instead of an empty box.
 */
const POSTER_SRC = "/newhero.jpg";

/** Blur held briefly so the first painted frame resolves rather than snaps in. */
const BLUR_SETTLE_MS = 1000;

export function CoverVideo({
  onPlaying,
  onVisibleChange,
}: {
  onPlaying: () => void;
  /** Fires whenever imagery is on screen — playing video or poster — so the
   *  mark can switch between white and theme-aware. */
  onVisibleChange: (visible: boolean) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [visible, setVisible] = useState(false);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    onVisibleChange(visible);
  }, [visible, onVisibleChange]);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(true), BLUR_SETTLE_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let cancelled = false;

    // Safari decides autoplay eligibility from the muted *attribute*, and React
    // sets it as a property. Assert both before any play attempt.
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute("muted", "");
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "true");

    const attempt = () => {
      if (cancelled) return;
      video.play().catch(() => {
        // Refused — Low Power Mode, or not enough buffered yet. The poster is
        // already on screen, so there is nothing to recover from here.
      });
    };

    // Poster counts as imagery, so the mark goes white as soon as either lands.
    const onHasImage = () => {
      if (!cancelled) setVisible(true);
    };
    const onIsPlaying = () => {
      if (cancelled) return;
      setVisible(true);
      onPlaying();
    };
    // Returning to a backgrounded tab pauses playback on iOS; resume then.
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") attempt();
    };

    video.addEventListener("loadeddata", onHasImage);
    video.addEventListener("playing", onIsPlaying);
    video.addEventListener("canplay", attempt);
    document.addEventListener("visibilitychange", onVisibilityChange);

    attempt();

    // A single fallback: the first tap anywhere satisfies the gesture
    // requirement on the rare device that refuses a muted autoplay outright.
    const onFirstTouch = () => attempt();
    document.addEventListener("touchstart", onFirstTouch, {
      once: true,
      passive: true,
    });

    return () => {
      cancelled = true;
      video.removeEventListener("loadeddata", onHasImage);
      video.removeEventListener("playing", onIsPlaying);
      video.removeEventListener("canplay", attempt);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      document.removeEventListener("touchstart", onFirstTouch);
    };
  }, [onPlaying]);

  return (
    <div
      className={cn(
        "pointer-events-none absolute top-1/2 left-1/2 aspect-video w-full",
        "-translate-x-1/2 -translate-y-1/2",
        "transition-[filter] duration-700",
        settled ? "blur-0" : "blur-[20px]"
      )}
      aria-hidden="true"
    >
      <video
        ref={videoRef}
        className="h-full w-full object-cover"
        src={VIDEO_SRC}
        poster={POSTER_SRC}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        controls={false}
      />
    </div>
  );
}
